import React, { useMemo, useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { db } from "./firebase";
import { doc, setDoc, getDoc } from "firebase/firestore";


export default function InventoryApp() {
  const [summary, setSummary] = useState([]);
  const [view, setView] = useState("summary");
  const [targets, setTargets] = useState(() => {
    const saved = localStorage.getItem("targets");
    return saved ? JSON.parse(saved) : {};
  });
  const [totalTarget, setTotalTarget] = useState(() => {
    const saved = localStorage.getItem("totalTarget");
    return saved ? parseFloat(saved) : 0;
  });

// Firebaseに保存
useEffect(() => {
  if (Object.keys(targets).length > 0) {
    setDoc(doc(db, "inventory", "targets"), targets);
  }
}, [targets]);

useEffect(() => {
  setDoc(doc(db, "inventory", "totalTarget"), { value: totalTarget });
}, [totalTarget]);

// 起動時にFirebaseからデータを読み取る
useEffect(() => {
  const fetchData = async () => {
    const targetsSnap = await getDoc(doc(db, "inventory", "targets"));
    if (targetsSnap.exists()) {
      setTargets(targetsSnap.data());
    }

    const totalSnap = await getDoc(doc(db, "inventory", "totalTarget"));
    if (totalSnap.exists()) {
      setTotalTarget(totalSnap.data().value);
    }
  };
  fetchData();
}, []);


  const normalizeText = (text) => {
    if (!text) return "";
    return text.toString().replace(/\s+/g, "").toUpperCase();
  };

  const toNumber = (v) => {
    if (v === null || v === undefined) return 0;
    const n = parseFloat(v.toString().replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined || num === "") return "";
    const n = Number(num);
    if (isNaN(n)) return num;
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const findKeyFlexible = (keys, patterns) => {
    return keys.find((key) =>
      patterns.some((p) => key.replace(/\s+/g, "").includes(p))
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!rows || rows.length === 0) {
        alert("Excelにデータがありません");
        return;
      }

      summarize(rows);
    };

    reader.readAsArrayBuffer(file);
  };

  const summarize = (rows) => {
    const keys = Object.keys(rows[0] || {});
    const warehouseKey = keys[2];
    const baseRows = warehouseKey
      ? rows.filter((r) => {
          const warehouse = (r[warehouseKey] || "").toString();
          return warehouse !== "FOC冷蔵庫" && warehouse !== "FNC他社";
        })
      : rows;

    const nameKey1 = findKeyFlexible(keys, ["商品名称1", "商品名称", "商品名称１", "品名"]);
    const nameKey2 = findKeyFlexible(keys, ["商品名称2", "商品名称２", "部位", "形態"]);
    const weightKey = findKeyFlexible(keys, ["現在重量", "重量", "グロス"]);
    const amountKey = findKeyFlexible(keys, ["金額", "販売金額", "金額(円)"]);
    const priceKey = findKeyFlexible(keys, ["初回仕入単価", "初回単価", "仕入単価", "単価"]);

    if (!nameKey1 || !weightKey || !amountKey || !priceKey) {
      alert("必要な列が検出できません。『商品名称』『重量』『金額』『単価』を確認してください。");
      return;
    }

    const order = [
      "南鮪",
      "養殖南鮪",
      "本鮪",
      "養殖本鮪",
      "目鉢鮪",
      "黄肌鮪",
      "黄肌鮪（海旋+陸凍）",
      "びんちょう鮪",
      "鰹",
      "その他",
    ];

    const result = order.map((group) => {
      const filtered = baseRows.filter((r) => {
        const code = String(r["商品コード"] || "").trim();

        const yellowfinCodes = [
          "260000023", "260000028", "260000035", "260000041",
          "260000047", "266100027", "266100041", "266100047"
        ];

        const yellowfinFrozenCodes = [
          "265500016","265500022","265500065","465300002","465300008",
          "465300010","465300017","465300022","465300030","465400008",
          "465400010","465400022","465400030"
        ];

        if (group === "黄肌鮪") {
          return yellowfinCodes.includes(code);
        }

        if (group === "黄肌鮪（海旋+陸凍）") {
          return yellowfinFrozenCodes.includes(code);
        }

        const combined = normalizeText(`${r[nameKey1]}${r[nameKey2] || ""}`);
        if (group === "南鮪") {
          return combined.includes("南鮪") && !combined.includes("養殖");
        }
        if (group === "本鮪") {
          return combined.includes("本鮪") && !combined.includes("養殖");
        }
        if (group === "その他") {
          return !order.slice(0, -1).some((keyword) => combined.includes(keyword));
        }

        return combined.includes(group);
      });

      const totalQty = filtered.reduce((s, r) => s + toNumber(r[weightKey]), 0);
      const totalAmt = filtered.reduce((s, r) => s + toNumber(r[amountKey]), 0);
      const avgUnitPrice = totalQty > 0 ? totalAmt / totalQty : 0;

      const missingWeight = filtered
        .filter((r) => toNumber(r[priceKey]) === 0)
        .reduce((s, r) => s + toNumber(r[weightKey]), 0);

      const missingAmt = avgUnitPrice * missingWeight;
      const totalWithMissing = totalAmt + missingAmt;

      return {
        品目: group,
        数量合計: Math.floor(totalQty),
        金額合計: Math.floor(totalAmt),
        未入力分: Math.floor(missingAmt),
        合計評価額: Math.floor(totalWithMissing),
      };
    });

    const totalRow = {
      品目: "合計",
      数量合計: result.reduce((s, r) => s + (r.数量合計 || 0), 0),
      金額合計: result.reduce((s, r) => s + (r.金額合計 || 0), 0),
      未入力分: result.reduce((s, r) => s + (r.未入力分 || 0), 0),
      合計評価額: result.reduce((s, r) => s + (r.合計評価額 || 0), 0),
    };

    setSummary([...result, totalRow]);
  };

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(summary);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "在庫集計");
    XLSX.writeFile(wb, "在庫集計結果.xlsx");
  };

  const targetRows = useMemo(() => summary.filter((r) => r?.品目 && r.品目 !== "合計"), [summary]);

  const totals = summary.slice(0, -1).reduce(
    (acc, r) => {
      const ratio = parseFloat(targets[r.品目]?.ratio) || 0;
      const goal = r.品目 === "その他" ? 0 : Math.floor((ratio / 100) * totalTarget);
      const diff = (r.合計評価額 || 0) - goal;
      return {
        数量合計: acc.数量合計 + (r.数量合計 || 0),
        金額合計: acc.金額合計 + (r.金額合計 || 0),
        未入力分: acc.未入力分 + (r.未入力分 || 0),
        合計評価額: acc.合計評価額 + (r.合計評価額 || 0),
        目標金額: acc.目標金額 + goal,
        目標差異: acc.目標差異 + diff,
      };
    },
    { 数量合計: 0, 金額合計: 0, 未入力分: 0, 合計評価額: 0, 目標金額: 0, 目標差異: 0 }
  );

  const totalGoal = targetRows.reduce((sum, r) => {
    const ratio = parseFloat(targets[r.品目]?.ratio) || 0;
    return sum + Math.floor((ratio / 100) * totalTarget);
  }, 0);

  return (
    <div style={{ padding: 20, fontFamily: "sans-serif" }}>
      <h1>在庫集計アプリ</h1>

      {view === "summary" ? (
        <>
          <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
          {summary.length > 0 && (
            <>
              <table border="1" cellPadding="5" style={{ marginTop: 20 }}>
                <thead>
                  <tr style={{ background: "#f0f0f0" }}>
                    <th>品目</th>
                    <th>数量合計 (kg)</th>
                    <th>金額合計 (円)</th>
                    <th>未入力分 (円)</th>
                    <th style={{ color: "#0b5394" }}>合計評価額 (円)</th>
                    <th style={{ background: "#fff2cc" }}>目標金額 (円)</th>
                    <th>目標差異</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.slice(0, -1).map((r, i) => {
                    const ratio = parseFloat(targets[r.品目]?.ratio) || 0;
                    const goal = r.品目 === "その他" ? 0 : Math.floor((ratio / 100) * totalTarget);
                    const diff = (r.合計評価額 || 0) - goal;
                    return (
                      <tr key={i}>
                        <td>{r.品目}</td>
                        <td align="right">{formatNumber(r.数量合計)}</td>
                        <td align="right">{formatNumber(r.金額合計)}</td>
                        <td align="right">{formatNumber(r.未入力分)}</td>
                        <td align="right" style={{ fontWeight: "bold", color: "#0b5394" }}>
                          {formatNumber(r.合計評価額)}
                        </td>
                        <td align="right" style={{ background: "#fff2cc", fontWeight: "bold" }}>
                          {formatNumber(goal)}
                        </td>
                        <td align="right" style={{ color: diff >= 0 ? "red" : "green", fontWeight: "bold" }}>
                          {formatNumber(diff)}
                        </td>
                      </tr>
                    );
                  })}

                  <tr style={{ background: "#f0f0f0", fontWeight: "bold" }}>
                    <td align="center">合計</td>
                    <td align="right">{formatNumber(totals.数量合計)}</td>
                    <td align="right">{formatNumber(totals.金額合計)}</td>
                    <td align="right">{formatNumber(totals.未入力分)}</td>
                    <td align="right" style={{ color: "#0b5394" }}>{formatNumber(totals.合計評価額)}</td>
                    <td align="right" style={{ background: "#fff2cc" }}>{formatNumber(totals.目標金額)}</td>
                    <td align="right" style={{ color: totals.目標差異 >= 0 ? "red" : "green" }}>{formatNumber(totals.目標差異)}</td>
                  </tr>
                </tbody>
              </table>

              <div style={{ marginTop: 20, display: "flex", gap: 12 }}>
                <button onClick={downloadExcel} style={{ padding: 10 }}>
                  Excel出力
                </button>
                <button onClick={() => setView("target")} style={{ padding: 10, fontWeight: "bold" }}>
                  目標値 入力
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <h2 style={{ margin: 0 }}>目標値 入力</h2>
            <label>
              全体目標金額 (円):
              <input
                type="text"
                value={formatNumber(totalTarget)}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/,/g, "");
                  setTotalTarget(toNumber(rawValue));
                }}
                style={{ width: 200, marginLeft: 10, padding: 6, background: "#fff2cc", textAlign: "right" }}
                placeholder="例：4,000,000,000"
              />
            </label>
          </div>

          {targetRows.length === 0 ? (
            <p style={{ marginTop: 12 }}>先に在庫Excelをアップロードして、品目一覧を表示してください。</p>
          ) : (
            <table border="1" cellPadding="5" style={{ marginTop: 20 }}>
              <thead>
                <tr>
                  <th>品目</th>
                  <th style={{ background: "#fff2cc" }}>在庫割合 (%)</th>
                  <th style={{ background: "#fff2cc", width: 330 }}>目標金額 (円)</th>
                </tr>
              </thead>
              <tbody>
                {targetRows.map((r, i) => {
                  const ratio = parseFloat(targets[r.品目]?.ratio) || 0;
                  const goal = Math.floor((ratio / 100) * totalTarget);
                  return (
                    <tr key={i}>
                      <td>{r.品目}</td>
                      <td style={{ background: "#fff2cc" }}>
                        <input
                          type="number"
                          step="0.1"
                          value={targets[r.品目]?.ratio ?? ""}
                          onChange={(e) =>
                            setTargets((prev) => ({
                              ...prev,
                              [r.品目]: {
                                ...prev[r.品目],
                                ratio: e.target.value,
                              },
                            }))
                          }
                          style={{ width: 100, padding: 6, textAlign: "right" }}
                          placeholder="例：10.5"
                        />
                      </td>
                      <td style={{ background: "#fff2cc", textAlign: "right", fontWeight: "bold", width: 330 }}>
                        {formatNumber(goal)}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background: "#f0f0f0", fontWeight: "bold" }}>
                  <td align="center">合計</td>
                  <td></td>
                  <td style={{ background: "#fff2cc", textAlign: "right", fontWeight: "bold" }}>
                    {formatNumber(totalGoal)}
                  </td>
                </tr>
                </tbody>
            </table>
          )}
          <button
            style={{ marginTop: 20, padding: 10 }}
            onClick={() => setView("summary")}
          >
            ← 戻る
          </button>
        </div>
      )}
    </div>
  );
}

